import React, { useState, useRef, useEffect } from 'react';
import { Settings, Moon, Sun } from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';
import { useTranslation } from '../hooks/useTranslation';

interface SettingsMenuProps {
  isDarkMode: boolean;
  onThemeChange: () => void;
}

// Use Unicode Regional Indicator Symbols for flags
const flags = {
  en: '🇬🇧', // GB flag
  ar: '🇦🇪', // UAE flag
  ru: '🇷🇺', // Russia flag
};

export function SettingsMenu({ isDarkMode, onThemeChange }: SettingsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { setLanguage } = useLanguageStore();
  const { t, language } = useTranslation();

  const startCloseTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 3000);
  };

  const stopCloseTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      stopCloseTimer();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      startCloseTimer();
    }
  }, [isOpen]);

  const handleLanguageChange = (newLanguage: 'en' | 'ar' | 'ru') => {
    setLanguage(newLanguage);
    setIsOpen(false);
  };

  return (
    <div 
      className="relative" 
      ref={menuRef}
      onMouseEnter={stopCloseTimer}
      onMouseLeave={startCloseTimer}
      dir="ltr" // Force LTR layout regardless of language
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
      >
        <Settings className="h-5 w-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50">
          {/* Theme Toggle */}
          <button
            onClick={() => {
              onThemeChange();
              setIsOpen(false);
            }}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {isDarkMode ? (
              <>
                <Sun className="w-4 h-4 mr-2" />
                <span>Light Mode</span>
              </>
            ) : (
              <>
                <Moon className="w-4 h-4 mr-2" />
                <span>Dark Mode</span>
              </>
            )}
          </button>

          {/* Language Options */}
          <div className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1">
            <button
              onClick={() => handleLanguageChange('en')}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="inline-block w-6 text-base">{flags.en}</span>
              <span>{t.common.english}</span>
              {language === 'en' && <span className="ml-auto">✓</span>}
            </button>
            <button
              onClick={() => handleLanguageChange('ar')}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="inline-block w-6 text-base">{flags.ar}</span>
              <span>{t.common.arabic}</span>
              {language === 'ar' && <span className="ml-auto">✓</span>}
            </button>
            <button
              onClick={() => handleLanguageChange('ru')}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <span className="inline-block w-6 text-base">{flags.ru}</span>
              <span>{t.common.russian}</span>
              {language === 'ru' && <span className="ml-auto">✓</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}